import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { History, FileText, Download, Eye, Stethoscope, ScanEye, Calendar, Clock, ChevronRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DiagnosisResponse } from '../components/DiagnosisResponse';
import { PageHeader } from '../components/PageHeader';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const viewConsultation = async (consultation) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/history/${consultation.id}`);
      setSelectedConsultation(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadPDF = async (consultationId) => {
    setDownloadingId(consultationId);
    try {
      const response = await axios.get(`${API}/history/${consultationId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hippocrate_consultation_${consultationId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return timestamp;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'dermato':
        return <ScanEye className="w-5 h-5" />;
      default:
        return <Stethoscope className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'dermato':
        return 'Analyse Dermato';
      default:
        return 'Diagnostic IA';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'dermato':
        return 'bg-pink-600/20 text-pink-400 border-pink-500/30';
      default:
        return 'bg-indigo-600/20 text-indigo-400 border-indigo-500/30';
    }
  };

  return (
    <div data-testid="history-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <PageHeader icon={History} title="Historique des Consultations" subtitle="Traçabilité médico-légale de vos analyses IA" gradient="from-amber-600 to-orange-600" testId="history-header" />

      {/* Info Banner */}
      <div className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-200 font-medium">Traçabilité médico-légale</p>
            <p className="text-xs text-blue-300/80 mt-1">
              Chaque consultation IA est enregistrée avec horodatage. Vous pouvez exporter un rapport PDF 
              pour archivage dans le dossier patient.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* History List */}
        <div className="lg:col-span-1 glassmorphic rounded-2xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-400" />
            Consultations récentes
          </h2>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto mb-4 text-slate-700" />
              <p className="text-slate-500">Aucune consultation enregistrée</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-custom">
              {history.map((item) => (
                <button
                  key={item.id}
                  data-testid={`history-item-${item.id}`}
                  onClick={() => viewConsultation(item)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedConsultation?.id === item.id
                      ? 'bg-amber-600/20 border-amber-500/50'
                      : 'bg-slate-900/40 border-slate-800 hover:bg-slate-900/60 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`p-2 rounded-lg border ${getTypeColor(item.type)}`}>
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {item.symptoms?.slice(0, 50) || getTypeLabel(item.type)}
                        {item.symptoms?.length > 50 ? '...' : ''}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.timestamp)}
                        </span>
                        <span className="text-xs text-slate-600">•</span>
                        <span className="text-xs text-slate-500">
                          {formatTime(item.timestamp)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Consultation Detail */}
        <div className="lg:col-span-2 glassmorphic rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-slate-400" />
              Détail de la consultation
            </h2>
            {selectedConsultation && (
              <div className="flex gap-2">
                <Button
                  data-testid="download-pdf-button"
                  onClick={() => downloadPDF(selectedConsultation.id)}
                  disabled={downloadingId === selectedConsultation.id}
                  className="rounded-full px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm"
                >
                  {downloadingId === selectedConsultation.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export PDF
                </Button>
                <button
                  onClick={() => setSelectedConsultation(null)}
                  className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:bg-slate-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            </div>
          ) : !selectedConsultation ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <p className="text-slate-500">Sélectionnez une consultation pour voir les détails</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto scrollbar-custom">
              {/* Metadata */}
              <div className="flex flex-wrap gap-3 pb-4 border-b border-slate-800">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(selectedConsultation.type)}`}>
                  {getTypeLabel(selectedConsultation.type)}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-300">
                  {formatDate(selectedConsultation.timestamp)} à {formatTime(selectedConsultation.timestamp)}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-400">
                  ID: {selectedConsultation.id?.slice(0, 8)}...
                </span>
              </div>

              {/* Symptoms */}
              {selectedConsultation.symptoms && selectedConsultation.type !== 'dermato' && (
                <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800">
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Motif de consultation</h3>
                  <p className="text-white">{selectedConsultation.symptoms}</p>
                </div>
              )}

              {/* Response */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Réponse IA</h3>
                <DiagnosisResponse content={selectedConsultation.response} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
